import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { allDatasets, allRoles, isAdmin, userData } from "../Common/Store/CommonSignals";
import LoadingScreen from "../Pages/LoadingScreen/LoadingScreen";

export const groupsMap = {
  ATLAS_PowerUser: "0497a4d4-a91f-4616-8bd6-b21eb7b64caf",
  ATLAS_Admin: "8007faf5-493c-4179-aa8e-017a49391c6c",
};

// Reverse-maps AAD group object IDs to the app's known group names.
// Used when the Graph app-only token lacks GroupMember.Read.All Application
// permission and can't return displayName for group memberships.
export const groupIdsToNames = (groupIds) => {
  if (!Array.isArray(groupIds)) return [];
  const idToName = Object.fromEntries(
    Object.entries(groupsMap).map(([name, id]) => [id, name])
  );
  return groupIds.map((id) => idToName[id]).filter(Boolean);
};

export const getRoles = (userGroups) => {
  // Convert user group names to GUIDs using the groupsMap
  const userGroupIds = userGroups.map((groupName) => groupsMap[groupName]).filter(Boolean);

  // Find all roles that match the user's group IDs
  const matchingRoles = allRoles.value.filter((role) =>
    role?.groupId?.some((roleGroupId) => userGroupIds.includes(roleGroupId))
  );
  console.log("matchingRoles:", matchingRoles);
  return matchingRoles;
};

export const getPermissions = (userGroups, roles) => {
  // Handle edge cases
  if (!userGroups || !Array.isArray(userGroups) || userGroups.length === 0) {
    console.warn("No user groups provided");
    return { ViewOnly: [], Modify: [] };
  }

  if (!roles || !Array.isArray(roles) || roles.length === 0) {
    console.warn("No roles provided");
    return { ViewOnly: [], Modify: [] };
  }

  // Convert user group names to GUIDs using the groupsMap
  const userGroupIds = userGroups.map((groupName) => groupsMap[groupName]).filter(Boolean); // Remove any undefined values

  console.log("User groups:", userGroups);
  console.log("User group IDs:", userGroupIds);

  // Find all roles that match the user's group IDs
  const matchingRoles = roles.filter((role) =>
    role?.groupId?.some((roleGroupId) => userGroupIds.includes(roleGroupId))
  );

  console.log("Matching roles:", matchingRoles);

  // Extract permissions from all matching roles
  const allPermissions = matchingRoles.flatMap((role) => role.permissions || []);

  // Group permissions by action type (ViewOnly, Modify)
  const permissions = ["ViewOnly", "Modify"].reduce((perms, action) => {
    perms[action] = allPermissions
      .filter((permission) => permission.action === action)
      .flatMap((permission) => permission.resourcesName || [])
      .filter((resourceName, index, array) => array.indexOf(resourceName) === index); // Remove duplicates
    return perms;
  }, {});

  console.log("Final permissions:", permissions);
  return permissions;
};

export function ProtectedRoute({ hasAccess, component, children }) {
  const [isAllowed, setIsAllowed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const userRolePerms = userData.value?.permissions;
  const userIsAdmin = isAdmin.value;
  const publicViewComponents = ["View Template", "View Dataset", "View Entry"];

  useEffect(() => {
    if (publicViewComponents.includes(component) || hasAccess || userIsAdmin) {
      setIsAllowed(true);
      setIsLoading(false);
    } else {
      setIsLoading(true);
      userRolePerms?.ViewOnly.includes(component) || userRolePerms?.Modify.includes(component)
        ? setIsAllowed(true)
        : setIsAllowed(false);
      setIsLoading(false);
    }
  }, [component]);
  if (isLoading) {
    return (
      <div>
        <LoadingScreen />
      </div>
    );
  }
  if (isAllowed) {
    return children;
  }

  return (
    <Navigate
      to="/"
      replace
    />
  );
}
ProtectedRoute.propTypes = {
  hasAccess: PropTypes.bool,
  component: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// Helper function to check if a user belongs to a User Group
export const userBelongsToUserGroup = (userEmailToCheck, userGroupId) => {
  const userGroup = allDatasets.value?.find(
    (ds) => ds.datasetType === "User Group" && ds.id === userGroupId
  );

  if (!userGroup) return false;

  const groupMembers =
    userGroup.values
      ?.map((v) => {
        try {
          const person = typeof v.value === "string" ? JSON.parse(v.value) : v.person;
          return person?.email?.toLowerCase();
        } catch {
          return null;
        }
      })
      .filter(Boolean) || [];

  return groupMembers.includes(userEmailToCheck.toLowerCase());
};

// Helper function to check if user has access to a resource (template or dataset)
export const userHasAccessToResource = (resource, userEmailToCheck) => {
  if (!resource || !userEmailToCheck) return false;

  const email = userEmailToCheck.toLowerCase();

  // Check if user is the owner
  if (resource.owner?.toLowerCase() === email) return true;

  // Check if user is in coOwners (individual)
  if (resource.coOwners?.some((co) => !co.isUserGroup && co.user?.toLowerCase() === email)) {
    return true;
  }

  // Check if user is in sharedPermission (individual)
  if (
    resource.sharedPermission?.some((sp) => !sp.isUserGroup && sp.entity?.toLowerCase() === email)
  ) {
    return true;
  }

  // Check if user belongs to any User Group in coOwners
  if (
    resource.coOwners?.some(
      (co) => co.isUserGroup && co.userGroupId && userBelongsToUserGroup(email, co.userGroupId)
    )
  ) {
    return true;
  }

  // Check if user belongs to any User Group in sharedPermission
  if (
    resource.sharedPermission?.some(
      (sp) => sp.isUserGroup && sp.userGroupId && userBelongsToUserGroup(email, sp.userGroupId)
    )
  ) {
    return true;
  }

  return false;
};
